import { $insuranceAxios } from '@insurance/api'
import { useQueryParams } from '@shared/hooks'
import { camelToSnake, downloadFile } from '@shared/utils'
import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query'
import { Button, ButtonProps } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { AxiosRequestConfig } from 'axios'
import { DocumentDownload } from 'iconsax-react'
import queryString from 'query-string'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type UseAsyncMutationProperty = UseMutateAsyncFunction<
  unknown,
  unknown,
  AxiosRequestConfig
>

type Props = {
  url: string
  name?: string
  params?: AnyObject
  buttonName?: React.ReactNode
  useQueryParams?: boolean
  multipleMutate?: (mutateAsync: UseAsyncMutationProperty) => void
  validateFn?: () => Promise<void>
  onSuccess?: () => void
  onError?: (error: unknown) => void
  onDownload?: () => AxiosRequestConfig | undefined
} & ButtonProps

export const ExportFile = ({
  url,
  params: initParams,
  name,
  buttonName,
  useQueryParams: initUseQueryParams = true,
  loading: btnLoading,
  multipleMutate,
  validateFn,
  onSuccess,
  onError,
  onDownload,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const params = useQueryParams()
  const [loading, setLoading] = useState(false)

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: async (config?: AxiosRequestConfig) => {
      const query = initUseQueryParams
        ? Object.fromEntries(
            Object.entries(params).map(([key, value]) => [
              camelToSnake(key),
              value
            ])
          )
        : {}

      await validateFn?.()

      return await $insuranceAxios({
        url: queryString.stringifyUrl({
          url,
          query: { ...query, ...initParams }
        }),
        responseType: 'blob',
        ...config
      })
    },

    onSuccess: ({ data }) => {
      setLoading(true)
      downloadFile(data, name).finally(() => {
        setLoading(false)
        onSuccess?.()
      })
    },
    onError
  })

  return (
    <Button
      type="primary"
      icon={<DocumentDownload size="16" />}
      onClick={() => {
        if (multipleMutate) {
          multipleMutate(mutateAsync as UseAsyncMutationProperty)
        } else {
          const config = onDownload?.()
          mutate(config)
        }
      }}
      loading={isPending || loading || btnLoading}
      {...props}
    >
      {buttonName ?? t('export')}
    </Button>
  )
}
